// Настройка адреса сервера на экране входа.
//
// ## Почему она здесь, а не в настройках внутри программы
//
// Пока адрес неверный, войти нельзя — значит и до настроек внутри не добраться. Программа,
// собранная один раз, ставится и на демонстрацию, и на боевой контур заказчика: адрес обязан
// задаваться снаружи, до входа.
//
// Панель свёрнута, пока адрес настроен и сервер отвечает: человеку, у которого всё работает,
// вопрос «куда ходить за данными» не нужен. Разворачивается сама, если связи нет — потому что
// в девяти случаях из десяти дело именно в адресе.

import { useState } from 'react';

import { RiCheckLine, RiErrorWarningLine, RiServerLine } from '@remixicon/react';

import {
  адресПодходитПланшету,
  checkServerAddress,
  serverAddress,
  serverAddressIsCustom,
  setServerAddress,
} from '@/shared/api/server-address';
import * as Button from '@/shared/ui/button';
import * as Hint from '@/shared/ui/hint';

export function ServerAddressPanel({
  развернуть,
  толькоЗащищённый = false,
}: {
  развернуть: boolean;
  /**
   * Планшет: принимаем только защищённый адрес.
   *
   * Не придирка к оформлению — обращение по открытому протоколу там блокирует сама система,
   * и выглядит это как «нет связи» при живом сервере.
   */
  толькоЗащищённый?: boolean;
}) {
  // `useState(развернуть)` прочитал бы свойство ОДИН раз, при первом построении. А ошибка
  // связи приходит позже — после первой попытки входа, — и панель осталась бы свёрнутой ровно
  // в тот момент, ради которого она разворачивается сама. Поэтому раскрытие хранится
  // отдельным признаком «человек уже трогал панель», а до этого решает свойство.
  const [трогали, setТрогали] = useState(false);
  const [открытоРукой, setОткрытоРукой] = useState(false);
  const открыта = трогали ? открытоРукой : развернуть;

  const setОткрыта = (значение: boolean) => {
    setТрогали(true);
    setОткрытоРукой(значение);
  };
  const [адрес, setАдрес] = useState(serverAddress());
  const [проверяем, setПроверяем] = useState(false);
  const [итог, setИтог] = useState<{ ok: boolean; текст: string } | null>(null);

  async function сохранить() {
    setПроверяем(true);
    setИтог(null);
    try {
      if (толькоЗащищённый && !адресПодходитПланшету(адрес)) {
        setИтог({
          ok: false,
          текст:
            'Не сохранила: на планшете работает только защищённый адрес — тот, что начинается ' +
            'с https. По обычному адресу система не пустит, и это будет выглядеть как «нет ' +
            'связи» при живом сервере.',
        });
        return;
      }
      // Проверяем ДО сохранения: адрес с опечаткой, сохранённый молча, даст «нет связи» на
      // входе, и человек будет искать причину не там.
      const ответ = await checkServerAddress(адрес);
      if (!ответ.ok) {
        setИтог({ ok: false, текст: `Не сохранила: ${ответ.reason ?? 'сервер не ответил'}.` });
        return;
      }
      setServerAddress(адрес);
      setИтог({ ok: true, текст: 'Адрес сохранён. Можно входить.' });
    } finally {
      setПроверяем(false);
    }
  }

  if (!открыта) {
    return (
      <button
        type='button'
        onClick={() => setОткрыта(true)}
        className='flex items-center justify-center gap-1 text-paragraph-xs text-text-sub-600'
      >
        <RiServerLine className='size-3.5' />
        {serverAddressIsCustom() ? 'Сервер: ' : 'Сервер по умолчанию: '}
        {/* Пустой адрес — «тот же, что у страницы»: показываем адрес страницы, а не пустоту. */}
        {serverAddress() || window.location.origin}
      </button>
    );
  }

  return (
    <div className='flex flex-col gap-2 rounded-lg bg-bg-weak-50 p-3'>
      <label htmlFor='server-address' className='text-label-xs text-text-sub-600'>
        Адрес сервера
      </label>
      <input
        id='server-address'
        value={адрес}
        onChange={(событие) => setАдрес(событие.target.value)}
        /* Клавиатура адреса: точка и косая черта под пальцем, без заглавной буквы и без
           автозамены. Без этого планшет предлагает «Bark-demo.uxcode.agency» и правит
           «uxcode» на что-нибудь словарное. */
        type='url'
        inputMode='url'
        autoCapitalize='none'
        autoCorrect='off'
        spellCheck={false}
        placeholder={толькоЗащищённый ? 'bark.example.ru' : 'bark.example.ru или 192.168.1.10:8000'}
        className='h-9 rounded-10 border border-stroke-soft-200 bg-bg-white-0 px-3 text-paragraph-sm text-text-strong-950 outline-none placeholder:text-text-soft-400 focus:border-stroke-sub-300'
      />
      <span className='text-paragraph-xs text-text-sub-600'>
        Это адрес сервера вашей организации. Его выдаёт администратор; программа запомнит его и
        больше спрашивать не будет.
      </span>
      <div className='flex items-center gap-2'>
        <Button.Root
          type='button'
          variant='neutral'
          mode='stroke'
          size='xsmall'
          onClick={() => void сохранить()}
        >
          {проверяем ? 'Проверяем связь…' : 'Проверить и сохранить'}
        </Button.Root>
        <Button.Root
          type='button'
          variant='neutral'
          mode='ghost'
          size='xsmall'
          onClick={() => setОткрыта(false)}
        >
          Свернуть
        </Button.Root>
      </div>
      {итог === null ? null : итог.ok ? (
        <span className='flex items-center gap-1 text-paragraph-xs text-success-base'>
          <RiCheckLine className='size-3.5' />
          {итог.текст}
        </span>
      ) : (
        // Отказ рисуется как отказ. До 19.09.2026 неудачная проверка адреса выглядела
        // серым примечанием того же кегля, что и пояснение под полем: человек нажимал
        // «Проверить и сохранить», ничего не замечал и шёл вводить имя на прежний сервер.
        <Hint.Root hasError>
          <Hint.Icon as={RiErrorWarningLine} />
          {итог.текст}
        </Hint.Root>
      )}
    </div>
  );
}
